import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FindStudyHelper } from '@/admin/groups/FindStudyHelper';
import api from '@/api';

/**
 * Multi-value study-helper picker for a filter field.
 *
 * `FindStudyHelper` is single-select (`selectedId`), but the list endpoints
 * take `list[int]`, so the chosen helpers show as chips above it and each pick
 * appends one. The value is the ids as strings, the shape every other
 * multiselect filter produces.
 */
export function HelperFilter({
  value,
  onChange,
}: {
  value: string[];
  onChange: (value: string[]) => void;
}): ReactElement {
  const { t } = useTranslation();
  const ids = value.map(Number);

  return (
    <div data-testid="helper-filter">
      <ChipRow>
        {value.length === 0 ? (
          <Hint>{t('admin.common.helperFilter.none')}</Hint>
        ) : (
          value.map((id: string) => (
            <HelperChip
              key={id}
              id={Number(id)}
              onRemove={() => onChange(value.filter((v) => v !== id))}
            />
          ))
        )}
      </ChipRow>
      <FindStudyHelper
        selectedId={null}
        excludeIds={ids}
        onSelect={(id) => {
          if (!ids.includes(id)) onChange([...value, String(id)]);
        }}
      />
    </div>
  );
}

// A component per chip so the name lookup is a hook per chip, not a loop of
// hooks; react-query serves repeats from cache.
function HelperChip({
  id,
  onRemove,
}: {
  id: number;
  onRemove: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { data } = api.getStudyHelper.useQuery({
    path: { study_helper_id: id },
  });
  const label = data?.display_name || `#${id}`;

  return (
    <Chip>
      {label}
      <ChipRemove
        type="button"
        aria-label={t('admin.common.helperFilter.remove', {
          name: label,
          interpolation: { escapeValue: false },
        })}
        onClick={onRemove}
      >
        &times;
      </ChipRemove>
    </Chip>
  );
}

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.5rem;
`;

const Hint = styled.span`
  color: var(--grey-600);
  font-size: 0.8rem;
`;

const Chip = styled.span`
  align-items: center;
  background: var(--grey-100);
  border: 1px solid var(--grey-300);
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.8rem;
  gap: 0.35rem;
  padding: 0.15rem 0.5rem;
`;

const ChipRemove = styled.button`
  background: none;
  border: 0;
  color: var(--grey-600);
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  padding: 0;
`;
